import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';

import Form, { FormInput } from 'components/common/Form';
import { AuthForgotSchemaType } from 'store/auth';
import { forgotSchema } from 'configs/validationSchemes';
import useYupValidationResolver from 'hooks/useYupValidationResolver';
import Modal from 'components/common/Modal';
import DotIcon from 'components/common/icons/Dot';
import useScreen from 'hooks/useScreen';
import { TranslationKey } from 'utils/translate';
import useToaster from 'hooks/useToaster';
import { forgotPassword } from 'api/requests/account';

import sx from './styles';

export interface IForgotPasswordModalProps {
  onClose: () => void;
}

const ForgotPasswordModal: FC<IForgotPasswordModalProps> = ({ onClose }) => {
  const notify = useToaster();
  const { isMobile } = useScreen();
  const [loading, setLoading] = useState(false);

  const resolver = useYupValidationResolver<{ email: string }>(forgotSchema);
  const { control, handleSubmit } = useForm<AuthForgotSchemaType>({
    resolver,
  });

  const handleForgotSubmit = async (data: AuthForgotSchemaType): Promise<void> => {
    setLoading(true);
    try {
      await forgotPassword(data);
      onClose();
      notify({
        message: 'Success',
        type: 'success',
      });
    } catch (error) {
      notify({
        message: ((error as Error)?.message || 'Something went wrong') as TranslationKey,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (): Promise<void> => {
    handleSubmit(handleForgotSubmit)();
  };

  return (
    <Modal
      title="Forgot Password"
      icon={<DotIcon fill="red.scale.300" />}
      saveText="Apply"
      isLoading={loading}
      onSave={onSave}
      onClose={onClose}
      maxWidth={isMobile ? 600 : 400}
    >
      <Form sx={sx.form} spacing={2.5} onSubmit={handleSubmit(handleForgotSubmit)}>
        <FormInput<AuthForgotSchemaType> required label="Username or Email" control={control} name="email" />
      </Form>
    </Modal>
  );
};

export default ForgotPasswordModal;
